/**
 * HSM/KMS Key Management Zustand Store
 *
 * State management for:
 * - HSM keys (list, CRUD operations)
 * - Provider configurations
 * - Dashboard statistics
 * - Audit log entries
 *
 * Phase 3: HSM/KMS Integration
 * Author: Claude Opus 4.5
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ============================================================================
// TYPES
// ============================================================================

/** HSM provider types */
export type HsmProvider =
  | 'aws_kms'
  | 'azure_key_vault'
  | 'gcp_kms'
  | 'hardware_hsm'
  | 'software_hsm';

/** Key purpose types */
export type KeyPurpose =
  | 'jwt_signing'
  | 'ca_root'
  | 'device_encryption'
  | 'data_encryption'
  | 'digital_signature'
  | 'key_exchange'
  | 'attestation';

/** Key algorithm types */
export type KeyAlgorithm =
  | 'RSA_2048'
  | 'RSA_4096'
  | 'ECDSA_P256'
  | 'ECDSA_P384'
  | 'ED25519'
  | 'AES_256_GCM'
  | 'KYBER_1024'
  | 'DILITHIUM_5'
  | 'HYBRID_KYBER_X25519';

/** Key status types */
export type KeyStatus =
  | 'active'
  | 'pending'
  | 'rotation_pending'
  | 'rotated'
  | 'revoked'
  | 'deletion_pending'      // Key deletion awaiting approval (H-001)
  | 'scheduled_for_deletion' // Key scheduled for purge (H-002)
  | 'deleted';

/** Provider health status */
export type ProviderHealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

/** HSM Key interface */
export interface HsmKey {
  keyId: string;
  name: string;
  purpose: KeyPurpose;
  algorithm: KeyAlgorithm;
  provider: HsmProvider;
  status: KeyStatus;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  rotationScheduledAt?: string;
  lastRotatedAt?: string;
  rotationPolicyDays: number;
  publicKeyPem?: string;
  version: number;
  metadata?: Record<string, unknown>;
  // Deletion/recovery fields (H-001, H-002)
  deletionRequestedAt?: string;
  deletionRequestedBy?: string;
  scheduledPurgeAt?: string;
  deletionApprovalWorkflowId?: string;
  deletionFirstApprover?: string;
  deletionSecondApprover?: string;
  recoverableUntil?: string;
}

/** Key deletion status */
export interface KeyDeletionStatus {
  keyId: string;
  status: KeyStatus;
  deletionRequestedAt?: string;
  deletionRequestedBy?: string;
  scheduledPurgeAt?: string;
  firstApprover?: string;
  firstApprovedAt?: string;
  secondApprover?: string;
  secondApprovedAt?: string;
  canRecover: boolean;
  daysUntilPurge?: number;
}

/** Request key deletion */
export interface RequestKeyDeletionRequest {
  reason: string;
  retentionDays?: number;
}

/** Approve key deletion */
export interface ApproveKeyDeletionRequest {
  comment?: string;
}

/** Reject key deletion */
export interface RejectKeyDeletionRequest {
  reason: string;
}

/** Recover key request */
export interface RecoverKeyRequest {
  reason: string;
}

/** Provider status interface */
export interface ProviderStatus {
  provider: HsmProvider;
  enabled: boolean;
  healthStatus: ProviderHealthStatus;
  lastHealthCheck: string;
  endpoint?: string;
  region?: string;
  keyCount: number;
}

/** Key audit log entry */
export interface KeyAuditLogEntry {
  entryId: string;
  keyId: string;
  operation: KeyOperation;
  userId: string;
  userEmail: string;
  sourceIp?: string;
  timestamp: string;
  details: Record<string, unknown>;
  success: boolean;
  errorMessage?: string;
}

/** Key operations for audit */
export type KeyOperation =
  | 'create'
  | 'rotate'
  | 'revoke'
  | 'delete'
  | 'update_metadata'
  | 'export'
  | 'import'
  | 'view_details';

/** Dashboard statistics */
export interface HsmDashboardStats {
  totalKeys: number;
  activeKeys: number;
  pendingRotation: number;
  revokedKeys: number;
  /** Keys pending deletion approval (H-001) */
  pendingDeletionApproval: number;
  /** Keys scheduled for deletion (H-002) */
  scheduledForDeletion: number;
  keysByPurpose: Record<string, number>;
  keysByProvider: Record<string, number>;
  keysByAlgorithm: Record<string, number>;
  providerHealth: ProviderStatus[];
  recentOperations: KeyAuditLogEntry[];
}

/** Create key request */
export interface CreateKeyRequest {
  name: string;
  purpose: KeyPurpose;
  algorithm: KeyAlgorithm;
  provider?: HsmProvider;
  rotationPolicyDays?: number;
  description?: string;
  metadata?: Record<string, unknown>;
}

/** Rotate key request */
export interface RotateKeyRequest {
  reason?: string;
  force?: boolean;
}

/** Revoke key request */
export interface RevokeKeyRequest {
  reason: string;
}

/** Update key metadata request */
export interface UpdateKeyMetadataRequest {
  name?: string;
  rotationPolicyDays?: number;
  metadata?: Record<string, unknown>;
}

/** Provider configuration update */
export interface UpdateProviderConfigRequest {
  enabled?: boolean;
  endpoint?: string;
  region?: string;
  config?: Record<string, unknown>;
}

/** Key list filters */
export interface KeyListFilters {
  purpose?: KeyPurpose;
  algorithm?: KeyAlgorithm;
  status?: KeyStatus;
  provider?: HsmProvider;
  ownerId?: string;
}

// ============================================================================
// STORE STATE
// ============================================================================

interface HsmState {
  // Keys
  keys: HsmKey[];
  selectedKey: HsmKey | null;
  keysLoading: boolean;
  keysError: string | null;
  
  // Providers
  providers: ProviderStatus[];
  providersLoading: boolean;
  providersError: string | null;
  
  // Dashboard
  dashboard: HsmDashboardStats | null;
  dashboardLoading: boolean;
  dashboardError: string | null;
  
  // Audit Log
  auditLog: KeyAuditLogEntry[];
  auditLoading: boolean;
  auditError: string | null;
  
  // Filters
  filters: KeyListFilters;
  
  // Pagination
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface HsmActions {
  // Keys
  setKeys: (keys: HsmKey[]) => void;
  addKey: (key: HsmKey) => void;
  updateKey: (keyId: string, updates: Partial<HsmKey>) => void;
  removeKey: (keyId: string) => void;
  setSelectedKey: (key: HsmKey | null) => void;
  setKeysLoading: (loading: boolean) => void;
  setKeysError: (error: string | null) => void;
  
  // Providers
  setProviders: (providers: ProviderStatus[]) => void;
  updateProvider: (provider: HsmProvider, updates: Partial<ProviderStatus>) => void;
  setProvidersLoading: (loading: boolean) => void;
  setProvidersError: (error: string | null) => void;
  
  // Dashboard
  setDashboard: (stats: HsmDashboardStats | null) => void;
  setDashboardLoading: (loading: boolean) => void;
  setDashboardError: (error: string | null) => void;
  
  // Audit Log
  setAuditLog: (entries: KeyAuditLogEntry[]) => void;
  addAuditEntry: (entry: KeyAuditLogEntry) => void;
  setAuditLoading: (loading: boolean) => void;
  setAuditError: (error: string | null) => void;
  
  // Filters
  setFilters: (filters: KeyListFilters) => void;
  clearFilters: () => void;
  
  // Pagination
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setPagination: (total: number, totalPages: number) => void;
  
  // Reset
  reset: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: HsmState = {
  keys: [],
  selectedKey: null,
  keysLoading: false,
  keysError: null,
  
  providers: [],
  providersLoading: false,
  providersError: null,
  
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
  
  auditLog: [],
  auditLoading: false,
  auditError: null,
  
  filters: {},
  
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
};

// ============================================================================
// STORE
// ============================================================================

export const useHsmStore = create<HsmState & HsmActions>()(
  devtools(
    subscribeWithSelector(
      immer((set) => ({
        ...initialState,

        // Keys
        setKeys: (keys) =>
          { set((state) => {
            state.keys = keys;
            state.keysLoading = false;
            state.keysError = null;
          }); },

        addKey: (key) =>
          { set((state) => {
            state.keys.unshift(key);
            state.total += 1;
          }); },

        updateKey: (keyId, updates) =>
          { set((state) => {
            const index = state.keys.findIndex((k) => k.keyId === keyId);
            if (index !== -1 && state.keys[index]) {
              // Safe spread: existing key has all required fields, updates are partial
              Object.assign(state.keys[index], updates);
            }
            if (state.selectedKey?.keyId === keyId) {
              Object.assign(state.selectedKey, updates);
            }
          }); },

        removeKey: (keyId) =>
          { set((state) => {
            state.keys = state.keys.filter((k) => k.keyId !== keyId);
            if (state.selectedKey?.keyId === keyId) {
              state.selectedKey = null;
            }
            state.total -= 1;
          }); },

        setSelectedKey: (key) =>
          { set((state) => {
            state.selectedKey = key;
          }); },

        setKeysLoading: (loading) =>
          { set((state) => {
            state.keysLoading = loading;
          }); },

        setKeysError: (error) =>
          { set((state) => {
            state.keysError = error;
            state.keysLoading = false;
          }); },

        // Providers
        setProviders: (providers) =>
          { set((state) => {
            state.providers = providers;
            state.providersLoading = false;
            state.providersError = null;
          }); },

        updateProvider: (provider, updates) =>
          { set((state) => {
            const index = state.providers.findIndex((p) => p.provider === provider);
            if (index !== -1 && state.providers[index]) {
              // Safe merge: existing provider has all required fields, updates are partial
              Object.assign(state.providers[index], updates);
            }
          }); },

        setProvidersLoading: (loading) =>
          { set((state) => {
            state.providersLoading = loading;
          }); },

        setProvidersError: (error) =>
          { set((state) => {
            state.providersError = error;
            state.providersLoading = false;
          }); },

        // Dashboard
        setDashboard: (stats) =>
          { set((state) => {
            state.dashboard = stats;
            state.dashboardLoading = false;
            state.dashboardError = null;
          }); },

        setDashboardLoading: (loading) =>
          { set((state) => {
            state.dashboardLoading = loading;
          }); },

        setDashboardError: (error) =>
          { set((state) => {
            state.dashboardError = error;
            state.dashboardLoading = false;
          }); },

        // Audit Log
        setAuditLog: (entries) =>
          { set((state) => {
            state.auditLog = entries;
            state.auditLoading = false;
            state.auditError = null;
          }); },

        addAuditEntry: (entry) =>
          { set((state) => {
            state.auditLog.unshift(entry);
          }); },

        setAuditLoading: (loading) =>
          { set((state) => {
            state.auditLoading = loading;
          }); },

        setAuditError: (error) =>
          { set((state) => {
            state.auditError = error;
            state.auditLoading = false;
          }); },

        // Filters
        setFilters: (filters) =>
          { set((state) => {
            state.filters = filters;
            state.page = 1; // Reset to first page when filters change
          }); },

        clearFilters: () =>
          { set((state) => {
            state.filters = {};
            state.page = 1;
          }); },

        // Pagination
        setPage: (page) =>
          { set((state) => {
            state.page = page;
          }); },

        setLimit: (limit) =>
          { set((state) => {
            state.limit = limit;
            state.page = 1; // Reset to first page when limit changes
          }); },

        setPagination: (total, totalPages) =>
          { set((state) => {
            state.total = total;
            state.totalPages = totalPages;
          }); },

        // Reset
        reset: () => { set(initialState); },
      }))
    ),
    { name: 'hsm-store' }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

/** Select all active keys */
export const selectActiveKeys = (state: HsmState): HsmKey[] =>
  state.keys.filter((k) => k.status === 'active');

/** Select keys pending rotation */
export const selectPendingRotationKeys = (state: HsmState): HsmKey[] =>
  state.keys.filter((k) => k.status === 'rotation_pending');

/** Select keys by provider */
export const selectKeysByProvider = (state: HsmState, provider: HsmProvider): HsmKey[] =>
  state.keys.filter((k) => k.provider === provider);

/** Select healthy providers */
export const selectHealthyProviders = (state: HsmState): ProviderStatus[] =>
  state.providers.filter((p) => p.healthStatus === 'healthy' && p.enabled);

/** Select keys with approaching rotation (within 7 days) */
export const selectKeysNeedingRotation = (state: HsmState): HsmKey[] => {
  const now = new Date();
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  
  return state.keys.filter((k) => {
    if (k.status !== 'active' || !k.rotationScheduledAt) {return false;}
    const rotationDate = new Date(k.rotationScheduledAt);
    return rotationDate <= sevenDaysFromNow;
  });
};

/** Select keys pending deletion approval (H-001) */
export const selectKeysPendingDeletionApproval = (state: HsmState): HsmKey[] =>
  state.keys.filter((k) => k.status === 'deletion_pending');

/** Select keys scheduled for deletion (H-002) */
export const selectKeysScheduledForDeletion = (state: HsmState): HsmKey[] =>
  state.keys.filter((k) => k.status === 'scheduled_for_deletion');

/** Select recoverable keys (can still be recovered) */
export const selectRecoverableKeys = (state: HsmState): HsmKey[] => {
  const now = new Date();
  return state.keys.filter((k) => {
    if (k.status !== 'scheduled_for_deletion') {return false;}
    if (!k.recoverableUntil) {return true;}
    return new Date(k.recoverableUntil) > now;
  });
};

// ============================================================================
// UTILITIES
// ============================================================================

/** Get display name for provider */
export const getProviderDisplayName = (provider: HsmProvider): string => {
  const names: Record<HsmProvider, string> = {
    aws_kms: 'AWS KMS',
    azure_key_vault: 'Azure Key Vault',
    gcp_kms: 'Google Cloud KMS',
    hardware_hsm: 'Hardware HSM',
    software_hsm: 'Software HSM',
  };
  return names[provider] || provider;
};

/** Get display name for purpose */
export const getPurposeDisplayName = (purpose: KeyPurpose): string => {
  const names: Record<KeyPurpose, string> = {
    jwt_signing: 'JWT Signing',
    ca_root: 'CA Root Key',
    device_encryption: 'Device Encryption',
    data_encryption: 'Data Encryption',
    digital_signature: 'Digital Signature',
    key_exchange: 'Key Exchange',
    attestation: 'TPM Attestation',
  };
  return names[purpose] || purpose;
};

/** Get display name for algorithm */
export const getAlgorithmDisplayName = (algorithm: KeyAlgorithm): string => {
  const names: Record<KeyAlgorithm, string> = {
    RSA_2048: 'RSA 2048',
    RSA_4096: 'RSA 4096',
    ECDSA_P256: 'ECDSA P-256',
    ECDSA_P384: 'ECDSA P-384',
    ED25519: 'Ed25519',
    AES_256_GCM: 'AES-256-GCM',
    KYBER_1024: 'Kyber-1024 (PQC)',
    DILITHIUM_5: 'Dilithium-5 (PQC)',
    HYBRID_KYBER_X25519: 'Hybrid Kyber/X25519',
  };
  return names[algorithm] || algorithm;
};

/** Get status color class */
export const getStatusColor = (status: KeyStatus): string => {
  const colors: Record<KeyStatus, string> = {
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    rotation_pending: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    rotated: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
    deletion_pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    scheduled_for_deletion: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    deleted: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return colors[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
};

/** Get health status color class */
export const getHealthColor = (status: ProviderHealthStatus): string => {
  const colors: Record<ProviderHealthStatus, string> = {
    healthy: 'bg-green-500/20 text-green-400',
    degraded: 'bg-yellow-500/20 text-yellow-400',
    unhealthy: 'bg-red-500/20 text-red-400',
    unknown: 'bg-gray-500/20 text-gray-400',
  };
  return colors[status] || 'bg-gray-500/20 text-gray-400';
};

export default useHsmStore;

